import commonservice from "@apis/commonservice";
import pricingservice from "@apis/pricingservice";
import { CDN_UPLOAD_URL } from "@config";
import { useAppSelector } from "@hooks/redux";
import { Status } from "@models/common";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { sortByOrder } from "@utils/array";

import { RootState } from "./store";
import report from "@report";
import { isMutipleRegion } from "@pages/pricing/utils";

export interface MemberShipStaticItem {
  id: number;
  key: string;
  module: string;
  name: string;
  order: number;
  type: "MODULE" | "LIST" | "ASSETS" | "BENEFIT";
  trial: number | null;
  basic: number | null;
  standard: number | null;
  premium: number | null;
  expired: number | null;
  display: boolean;
  unit?: string;
  basicExtraUnit?: string;
  standardExtraUnit?: string;
  premiumExtraUnit?: string;
}

export type PRICING_REGION = "Indonesia" | "Malysia" | "Thailand" | "Vietnam";
export type PRICING_DURATION = "Monthly" | "Yearly";
export const PRICING_LEVEL_TRIAL = "Trial";
export const PRICING_LEVEL_BASIC = "Basic";
export const PRICING_LEVEL_STANDARD = "Standard";
export const PRICING_LEVEL_PREMIUM = "Premium";
export const PRICING_LEVELS = [PRICING_LEVEL_TRIAL, PRICING_LEVEL_BASIC, PRICING_LEVEL_STANDARD, PRICING_LEVEL_PREMIUM];
export type PRICING_LEVEL = "Trial" | "Basic" | "Standard" | "Premium";
export type GAS_STATION_KEY =
  | "AI.CREDITS"
  | "DOWNLOAD.VIDEO"
  | "ACCOUNT.SUBCOUNT"
  | "REPORT"
  | "LIVESTREAM.FULL_RECORD"
  | "DATA.EXPORT.NUM"
  | "CUSTOMIZED.DATA.EXPORT";

export const PRICING_LEVEL_MAP_INDEX = {
  Basic: 1,
  Standard: 2,
  Premium: 3,
};

export const SELECT_COUNTRY_IMAGE: { [key in number | string]: string } = {
  Global: `${CDN_UPLOAD_URL}/upload/a4833265.png`,
  "Southeast Asia": `${CDN_UPLOAD_URL}/upload/67af1e4b.png`,
  Ameu: `${CDN_UPLOAD_URL}/upload/807e73e7.png`,
};

export interface IND_PRODUCT {
  id: string;
  key: GAS_STATION_KEY;
  name: string;
  price: string;
  title: string;
}

export interface PRICING_SUBSCRIBE_PRODUCT {
  id: string;
  itemId: string;
  name: string;
  price: string;
}
export type ALL_SUBSCRIBE_PRODUCTS =
  | {
      [key in PRICING_REGION]: {
        [key in PRICING_DURATION]: {
          [key in PRICING_LEVEL]: PRICING_SUBSCRIBE_PRODUCT;
        };
      };
    }
  | {};

export interface SELECTED_SUBSCRIBE_PRODUCT {
  type: "MEMBER" | "INDEPENDENT";
  itemId: string;
  realprice: number;
  quantity: number;

  // 加油包类型
  region: string;
  key: GAS_STATION_KEY;
  name: string;
  mark: string;
  unit: string;

  // 会员套餐
  isSubscription: boolean;
  skuId: string;
  price: number;
  level: string;
  regionName: string;
  duration: "Monthly" | "Yearly";
  subId: string;
  subItemId: string;
  subPrice: string;
  subDiscount: string;
  subQuantity: number;
}

const MEMBER_FEATURES = [
  "OVERVIEW.POTENTIAL_LIST",
  "OVERVIEW.DETAIL",
  "OVERVIEW.LEVEL_THIRD",
  "OVERVIEW.DATA_RANGE",
  "VIDEO.LIST",
  "VIDEO.DETAIL",
  "LIVESTREAM.LIST",
  "LIVESTREAM.DETAIL",
  "PRODUCT.LIST",
  "PRODUCT.DETAIL",
  "CREATOR.LIST",
  "DATA.EXPORT",
  "CREATOR.DETAIL",
  "CREATOR.MCN_SIGN",
  "CREATOR.CONTACT",
  "CREATOR.COLLABORATED_SHOP",
  "FORYOU.TABLELIST",
  "FORYOU.COLLABORATED_SHOP",
  "FORYOU.HOT",
  "FORYOU.FOCUSCOUNT",
  "SHOP.LIST",
  "SHOP.DETAIL",
  "DOWNLOAD.VIDEO",
  "FILTER.CUSTOM_RANGE",
  "LIST.DATE_RANGE",
  "DETAIL.DATE_RANGE",
  "LIST.DATE_SPACING",
  "DETAIL.DATE_SPACING",
  "LIVESTREAM.FULL_RECORD",
  "LIST.CATE_CASCADER_LIST",
  "FILTER.CUSTOM_TIMES",
  "AD.ANALYSIS",
  "AD.CREATOR_DEBUT",
  "DETAIL.ACCESS_TIMES",
  "CUSTOMIZED.DATA.EXPORT",
  "AI.CREDITS",
  "DATA.EXPORT.NUM",
];

export interface MemberFeatureConfig {
  type: string;
  count: number;
  progress: null | {
    remain: number;
    total: number;
  };
  guidance: {
    actionType: { name: string; desc: string };
  };
  level: PRICING_LEVEL;
  expired: boolean;
}

export interface MemberShip {
  country: string;
  level: PRICING_LEVEL;
  start: string;
  end: string;
  expired: boolean;
  day: number;
  gmtCreated: string;
  endDate: string;
  startDate: string;
  type: null | "NORMAL" | "SUBSCRIPTION" | "ISSUE";
  regionType: "single" | "southeast" | "global";
}

interface MemberShipState {
  staticConfig: { status: Status } & { items: MemberShipStaticItem[] };
  dynamicConfig: {
    status: Status;
    [key: (typeof MEMBER_FEATURES)[number]]: any | MemberFeatureConfig;
  };
  duration: PRICING_DURATION;
  selectedProducts: SELECTED_SUBSCRIBE_PRODUCT[];
  indProducts: IND_PRODUCT[];
  allCountryMemberShip: { status: Status; items: MemberShip[] };
  showRefresh: boolean;
  redirectURL: string;
}

// const initialState: MemberShipState = {
//   staticConfig: { status: "idle", items: [] },
//   dynamicConfig: {
//     status: "idle",
//     "OVERVIEW.POTENTIAL_LIST": {},
//     "OVERVIEW.CATEGORY_CHART": {},
//     "OVERVIEW.LEARNING_LIST": {},
//     "VIDEO.LIST": {},
//     "VIDEO.DETAIL": {},
//     "LIVESTREAM.LIST": {},
//     "LIVESTREAM.DETAIL": {},
//     "PRODUCT.LIST": {},
//     "PRODUCT.DETAIL": {},
//     "DATA.EXPORT": {},
//     "CREATOR.LIST": {},
//     "CREATOR.DETAIL": {},
//     "CREATOR.MCN_SIGN": {},
//     "FORYOU.TABLELIST": {},
//     "FORYOU.COLLABORATED_SHOP": {},
//     "FORYOU.HOT": {},
//     "SHOP.LIST": {},
//     "SHOP.DETAIL": {},
//     "FORYOU.FOCUSCOUNT": {},
//     // 不通过全局features接口
//     "ACCOUNT.SUBCOUNT": {},
//     "FILTER.CUSTOM_RANGE": {},
//     "LIST.DATE_RANGE": {},
//     "DETAIL.DATE_RANGE": {},
//     "LIST.DATE_SPACING": {},
//     "DETAIL.DATE_SPACING": {},
//     "LIST.CATE_CASCADER_LIST": {},
//     "FILTER.CUSTOM_TIMES": {},
//     "DETAIL.ACCESS_TIMES": {},
//     "CUSTOMIZED.DATA.EXPORT": {},
//     "AI.CREDITS": {},
//     "DATA.EXPORT.NUM": {},
//   },
//   selectedProducts: [],
//   duration: "Yearly",
//   indProducts: [],
//   allCountryMemberShip: { status: "idle", items: [] },
//   showRefresh: false,
//   redirectURL: "",
// };
const initialStateMock: MemberShipState = {
  staticConfig: { status: "idle", items: [] },
  dynamicConfig: {
    status: "idle",
    "OVERVIEW.POTENTIAL_LIST": {},
    "OVERVIEW.CATEGORY_CHART": {},
    "OVERVIEW.LEARNING_LIST": {},
    "VIDEO.LIST": {},
    "VIDEO.DETAIL": {},
    "LIVESTREAM.LIST": {},
    "LIVESTREAM.DETAIL": {},
    "PRODUCT.LIST": {},
    "PRODUCT.DETAIL": {},
    "DATA.EXPORT": {},
    "CREATOR.LIST": {},
    "CREATOR.DETAIL": {},
    "CREATOR.MCN_SIGN": {},
    "FORYOU.TABLELIST": {},
    "FORYOU.COLLABORATED_SHOP": {},
    "FORYOU.HOT": {},
    "SHOP.LIST": {},
    "SHOP.DETAIL": {},
    "FORYOU.FOCUSCOUNT": {},
    // 不通过全局features接口
    "ACCOUNT.SUBCOUNT": {},
    "FILTER.CUSTOM_RANGE": {},
    "LIST.DATE_RANGE": {},
    "DETAIL.DATE_RANGE": {},
    "LIST.DATE_SPACING": {},
    "DETAIL.DATE_SPACING": {},
    "LIST.CATE_CASCADER_LIST": {},
    "FILTER.CUSTOM_TIMES": {},
    "DETAIL.ACCESS_TIMES": {},
    "CUSTOMIZED.DATA.EXPORT": {},
    "AI.CREDITS": {
      key: "AI.CREDITS",
      type: "BENEFIT",
      enable: null,
      count: 300,
      guidance: {
        actionType: {
          name: "Upgrade",
          desc: "Upgrade to view more data",
        },
        targetLevel: null,
        purchasePrices: null,
        target: null,
        message: "Member Trial has Expired",
        cause: null,
      },
      progress: {
        total: 131400,
        used: 22440,
        remain: 108960,
        full: false,
      },
    },
    "DATA.EXPORT.NUM": {},
  },
  selectedProducts: [],
  duration: "Yearly",
  indProducts: [],
  allCountryMemberShip: { status: "idle", items: [] },
  showRefresh: false,
  redirectURL: "",
};

export const fetchStaticConfig = createAsyncThunk("memberCenter/fetchMemebershipConfig", async () => {
  return await commonservice.fetchConfigs<{ membership: MemberShipStaticItem[] }>({ reqData: ["membership"] });
});

export const fetchDynamicConfig = createAsyncThunk(
  "memberCenter/fetchDynamicConfig",
  async ({ region }: { region: string }) => {
    return await pricingservice.getFeture({ country: region, list: MEMBER_FEATURES });
  },
);

export const fetchIndProducts = createAsyncThunk("memberCenter/fetchIndProducts", async () => {
  return await pricingservice.fetchIndProducts<IND_PRODUCT[]>();
});

export const createOrder = createAsyncThunk("memberCenter/createOrder", async (reqData) => {
  return await pricingservice.createOrder(reqData);
});

export const updateFeautreFocusCount = createAsyncThunk("memberCenter/focusCount", async (region: string) => {
  return await pricingservice.getFeture<{ "FORYOU.FOCUSCOUNT": MemberFeatureConfig }>({
    country: region,
    list: ["FORYOU.FOCUSCOUNT"],
  });
});

export const updateFeatureSearchCount = createAsyncThunk("memberCenter/searchCount", async (region: string) => {
  return await pricingservice.getFeture<{ "FILTER.CUSTOM_TIMES": MemberFeatureConfig }>({
    country: region,
    list: ["FILTER.CUSTOM_TIMES"],
  });
});

export const updateFeautreDetailCount = createAsyncThunk("memberCenter/detailCount", async (region: string) => {
  return await pricingservice.getFeture<{ "DETAIL.ACCESS_TIMES": MemberFeatureConfig }>({
    country: region,
    list: ["DETAIL.ACCESS_TIMES"],
  });
});

export const selectDetailCountRemain = (state: RootState) => {
  const dynamicConfig = state.memberCenterReducer.dynamicConfig;
  return dynamicConfig["DETAIL.ACCESS_TIMES"]?.progress || {};
};

export const updateCreatorExportCount = createAsyncThunk("memberCenter/creatorExport", async (region: string) => {
  return await pricingservice.getFeture<{ "CREATOR.EXPLORT.NUM": MemberFeatureConfig }>({
    country: region,
    list: ["DATA.EXPORT.NUM"],
  });
});

export const updateCustomizedExportData = createAsyncThunk("memberCenter/creatorExport", async (region: string) => {
  return await pricingservice.getFeture<{ "CUSTOMIZED.DATA.EXPORT": MemberFeatureConfig }>({
    country: region,
    list: ["CUSTOMIZED.DATA.EXPORT"],
  });
});

export const updateFeautreDownloadVideo = createAsyncThunk("memberCenter/downloadvideo", async (region) => {
  return await pricingservice.getFeture<{ "DOWNLOAD.VIDEO": MemberFeatureConfig }>({
    country: region,
    list: ["DOWNLOAD.VIDEO"],
  });
});

export const updateFeautreSubAccount = createAsyncThunk("memberCenter/subaccount", async () => {
  return await pricingservice.getFeture<{ "ACCOUNT.SUBCOUNT": MemberFeatureConfig }>({
    list: ["ACCOUNT.SUBCOUNT"],
  });
});

export const updateFeautreLivestreamFullRecord = createAsyncThunk("memberCenter/livestreamfullrecord", async () => {
  return await pricingservice.getFeture<{ "LIVESTREAM.FULL_RECORD": MemberFeatureConfig }>({
    list: ["LIVESTREAM.FULL_RECORD"],
  });
});

export const updateAiStudioCredit = createAsyncThunk("aiStudio", async (region: string) => {
  return await pricingservice.getFeture<{ "AI.CREDITS": MemberFeatureConfig }>({
    country: region,
    list: ["AI.CREDITS"],
  });
});

export const queryMemberships = createAsyncThunk("memberCenter/queryMemberships", async () => {
  return await commonservice.queryMemberships<MemberShip[]>();
});

export const memberCenterSlice = createSlice({
  name: "memberCenter",
  initialState: initialStateMock,
  reducers: {
    updateDuration: (state, action) => {
      state.duration = action.payload;
    },
    updateSubscribeProducts: (state, action) => {
      state.selectedProducts = action.payload;
    },
    updateShowRefresh: (state, action) => {
      state.showRefresh = action.payload;
    },
    updateRedirectURL: (state, action) => {
      state.redirectURL = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStaticConfig.pending, (state) => {
        state.staticConfig = { ...state.staticConfig, status: "loading" };
      })
      .addCase(fetchStaticConfig.fulfilled, (state, action) => {
        state.staticConfig = { items: sortByOrder(action.payload.membership), status: "succeeded" };
      })
      .addCase(fetchStaticConfig.rejected, (state) => {
        state.staticConfig = { ...state.staticConfig, status: "failed" };
      })
      .addCase(fetchDynamicConfig.pending, (state) => {
        state.dynamicConfig = { ...state.dynamicConfig, status: "loading" };
      })
      .addCase(fetchDynamicConfig.fulfilled, (state, action) => {
        state.dynamicConfig = {
          ...state.dynamicConfig,
          ...action.payload,
          status: "succeeded",
        };
      })
      .addCase(fetchDynamicConfig.rejected, (state) => {
        state.dynamicConfig = { ...state.dynamicConfig, status: "failed" };
        report.error("fetchDynamicConfig failed");
      })
      .addCase(fetchIndProducts.fulfilled, (state, action) => {
        state.indProducts = [...action.payload];
      })
      .addCase(updateFeautreFocusCount.fulfilled, (state, action) => {
        state.dynamicConfig = { ...state.dynamicConfig, ...action.payload };
      })
      .addCase(updateCreatorExportCount.fulfilled, (state, action) => {
        state.dynamicConfig = { ...state.dynamicConfig, ...action.payload };
      })
      .addCase(updateFeatureSearchCount.fulfilled, (state, action) => {
        state.dynamicConfig = { ...state.dynamicConfig, ...action.payload };
      })
      .addCase(updateFeautreDetailCount.fulfilled, (state, action) => {
        state.dynamicConfig = { ...state.dynamicConfig, ...action.payload };
      })
      .addCase(updateFeautreDownloadVideo.fulfilled, (state, action) => {
        state.dynamicConfig = { ...state.dynamicConfig, ...action.payload };
      })
      .addCase(updateFeautreSubAccount.fulfilled, (state, action) => {
        state.dynamicConfig = { ...state.dynamicConfig, ...action.payload };
      })
      .addCase(updateFeautreLivestreamFullRecord.fulfilled, (state, action) => {
        state.dynamicConfig = { ...state.dynamicConfig, ...action.payload };
      })
      .addCase(updateAiStudioCredit.fulfilled, (state, action) => {
        state.dynamicConfig = { ...state.dynamicConfig, ...action.payload };
      })
      .addCase(queryMemberships.fulfilled, (state, action) => {
        state.allCountryMemberShip = {
          status: "succeeded",
          items: action.payload || [],
        };
      });
  },
});

export const { updateDuration, updateSubscribeProducts, updateShowRefresh, updateRedirectURL } =
  memberCenterSlice.actions;

export const selectGasStationLiveRecord = (state: RootState) => {
  const gasStations = state.memberCenterReducer.indProducts;
  return gasStations.find((gasStation) => gasStation.key === "LIVESTREAM.FULL_RECORD");
};

export const selectLiveRecordRemain = (state: RootState) => {
  const dynamicConfig = state.memberCenterReducer.dynamicConfig;
  return dynamicConfig["LIVESTREAM.FULL_RECORD"]?.progress?.remain || 0;
};

export const selectDownLoadVideo = (state: RootState) => {
  const dynamicConfig = state.memberCenterReducer.dynamicConfig;
  if (dynamicConfig["DOWNLOAD.VIDEO"]?.progress?.total === -1) {
    return -1;
  }
  return dynamicConfig["DOWNLOAD.VIDEO"]?.progress?.remain || 0;
};

export const useSupportRegionOptions = () => {
  const { regionOptions } = useAppSelector((state) => state.configReducer);

  const returnRegionOptions = (countryCode: string[], selectCountryKey: string) => {
    if (!isMutipleRegion(selectCountryKey)) return [];

    if (selectCountryKey === "Global") {
      return regionOptions;
    }
    return countryCode?.map((code) => regionOptions.find((option) => option.value === code)) || [];
  };
  return returnRegionOptions;
};

const memberCenterReducer = memberCenterSlice.reducer;
export default memberCenterReducer;
